import React, { useCallback, useEffect, useState } from "react";
import { apiGet, apiPost } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { HardDrive, Trash2, Loader2, AlertTriangle, CheckCircle2, FileText, Database, ScrollText } from "lucide-react";
import { toast } from "sonner";

/**
 * DiskUsageCard
 * -------------
 * Admin-only System-Status card that surfaces `/api/admin/disk-usage`.
 * Shows the used% progress bar, per-category breakdown (PDFs, Mongo,
 * logs, cached reports), and a "Clean Now" button that hits
 * `/api/admin/disk-cleanup` to apply the PDF retention policy.
 *
 * When the disk level is `warn` (>=80%) or `critical` (>=90%) we
 * promote the progress bar colour and pop a dedicated action banner
 * so the admin can react before a write fails.
 */
const fmtBytes = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const levelBadgeClass = (level) => {
  if (level === "critical") return "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40";
  if (level === "warn")     return "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40";
  return "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/40";
};

const progressClass = (level) => {
  if (level === "critical") return "[&>div]:bg-[hsl(var(--destructive))]";
  if (level === "warn")     return "[&>div]:bg-[hsl(var(--warning))]";
  return "[&>div]:bg-[hsl(var(--success))]";
};

const DiskUsageCard = () => {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const s = await apiGet("/admin/disk-usage");
      setSnap(s);
    } catch (e) {
      // Silent — card will render a minimal "unavailable" state.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 60_000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const onClean = async () => {
    setCleaning(true);
    try {
      const res = await apiPost("/admin/disk-cleanup");
      const freedMb = (res.total_bytes_freed || 0) / 1024 / 1024;
      toast.success(`Cleanup complete — freed ${freedMb.toFixed(1)} MB`, {
        description: `Analysed: ${res.stale_analysed_pdfs_deleted} · Skipped: ${res.skipped_pdfs_deleted} · Cap evictions: ${res.cap_evicted_pdfs}`,
      });
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const usedPct = snap?.used_pct ?? 0;
  const level = snap?.level || "ok";
  const levelIcon = level === "ok" ? CheckCircle2 : AlertTriangle;
  const LevelIcon = levelIcon;

  return (
    <Card className="border bg-card" data-testid="disk-usage-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-primary" />
            Disk Usage
            <Badge variant="outline" className={`text-[10px] gap-1 ${levelBadgeClass(level)}`} data-testid="disk-usage-level">
              <LevelIcon className="h-3 w-3" />
              {level.toUpperCase()}
            </Badge>
          </CardTitle>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                data-testid="disk-usage-clean-button"
                disabled={loading}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Clean Now
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Run disk cleanup now?</AlertDialogTitle>
                <AlertDialogDescription>
                  Applies the retention policy immediately:
                  <ul className="list-disc list-inside mt-2 text-xs space-y-1">
                    <li>Delete PDFs for reports completed &gt; 7 days ago</li>
                    <li>Delete PDFs for admin-skipped rows</li>
                    <li>Enforce a hard cap of 200 PDFs (oldest-first eviction)</li>
                    <li>Clear Python __pycache__ trees + empty report folders</li>
                  </ul>
                  <span className="block mt-3">
                    Analysis data stays in MongoDB — deleted PDFs can be re-downloaded from CSE on demand.
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={cleaning}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onClean}
                  disabled={cleaning}
                  className="gap-1.5"
                  data-testid="disk-usage-clean-confirm"
                >
                  {cleaning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  Clean now
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !snap && (
          <div className="text-sm text-muted-foreground">Loading disk usage…</div>
        )}
        {snap && (
          <>
            {/* Used progress bar */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-muted-foreground">
                  Used: <span className="font-semibold text-foreground" data-testid="disk-usage-used">
                    {usedPct.toFixed(1)}%
                  </span>{" "}
                  ({snap.used_gb} GB / {snap.total_gb} GB)
                </span>
                <span className="text-muted-foreground">
                  Free: <span className="font-semibold text-foreground">{snap.free_gb} GB</span>
                </span>
              </div>
              <Progress value={usedPct} className={`h-2 ${progressClass(level)}`} />
            </div>

            {/* Warn / critical banner */}
            {snap.recommended_action && (
              <div
                className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
                  level === "critical"
                    ? "border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"
                    : "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]"
                }`}
                data-testid="disk-usage-alert"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="text-foreground">{snap.recommended_action}</div>
              </div>
            )}

            {/* Breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <BreakdownStat
                icon={FileText}
                label="PDF reports"
                value={fmtBytes(snap.pdf_dir_bytes)}
                sub={`${snap.pdf_count} files`}
                testid="disk-usage-pdfs"
              />
              <BreakdownStat
                icon={FileText}
                label="Cached reports"
                value={fmtBytes(snap.cached_reports_bytes)}
                sub="archived extracts"
                testid="disk-usage-cached"
              />
              <BreakdownStat
                icon={Database}
                label="MongoDB"
                value={fmtBytes(snap.mongodb_bytes)}
                sub="structured data"
                testid="disk-usage-mongo"
              />
              <BreakdownStat
                icon={ScrollText}
                label="Logs"
                value={fmtBytes(snap.logs_bytes)}
                sub="supervisor"
                testid="disk-usage-logs"
              />
            </div>

            <div className="text-[10px] text-muted-foreground">
              Daily cleanup runs automatically at 02:00 SLT. Retention: 7 days for analysed PDFs, 200 hard cap.
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const BreakdownStat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3 w-3" />
      {label}
    </div>
    <div className="text-sm font-semibold tabular-nums mt-0.5">{value}</div>
    <div className="text-[10px] text-muted-foreground">{sub}</div>
  </div>
);

export default DiskUsageCard;
